import React, { useState, useCallback, useEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import { Prompt } from 'react-router-dom'
import { makeStyles } from '@material-ui/core/styles'
import SydButton from '../../../../../commonDesign/Button'
import { normalizeDefinition } from '../utils'
import PipelineConfig from './PipelineConfig'
import FieldMappingBuilder from './FieldMappingBuilder'
import ArgMappingEditor from './ArgMappingEditor'
import InvalidationsEditor from './InvalidationsEditor'
import RawDefinitionEditor from './RawDefinitionEditor'

const useStyles = makeStyles(() => ({
  toolbar: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '20px'
  },
  modeToggle: {
    display: 'flex',
    gap: '2px',
    backgroundColor: 'var(--color-neutral-100)',
    borderRadius: 'var(--border-radius-sm)',
    padding: '2px'
  },
  modeBtn: {
    padding: '4px 12px',
    fontSize: 'var(--font-size-xs)',
    fontWeight: 600,
    borderRadius: 'var(--border-radius-sm)',
    border: 'none',
    cursor: 'pointer',
    transition: 'all 0.15s',
    backgroundColor: 'transparent',
    color: 'var(--text-color-subtle)'
  },
  modeBtnActive: {
    backgroundColor: 'var(--color-surface-000)',
    color: 'var(--text-color-primary)',
    boxShadow: '0 1px 2px rgba(0,0,0,0.08)'
  },
  section: {
    marginBottom: '20px'
  },
  bottomRow: {
    display: 'flex',
    gap: '16px',
    '& > *': {
      flex: 1,
      minWidth: 0
    }
  }
}))

function DefinitionTab ({ template, onSave, onDefinitionChange, initialDefinition }) {
  const classes = useStyles()
  const [editorMode, setEditorMode] = useState('visual')

  const existingDefinition = template?.definition || {}
  const serializedExisting = useRef(JSON.stringify(normalizeDefinition(existingDefinition)))
  serializedExisting.current = JSON.stringify(normalizeDefinition(existingDefinition))

  const [definition, setDefinition] = useState(() => initialDefinition || normalizeDefinition(existingDefinition))

  const isDirty = JSON.stringify(definition) !== serializedExisting.current

  useEffect(() => {
    if (onDefinitionChange) {
      onDefinitionChange(definition)
    }
  }, [definition, onDefinitionChange])

  const updateField = useCallback((field, value) => {
    setDefinition(prev => ({ ...prev, [field]: value }))
  }, [])

  const handleRawChange = useCallback((rawDef) => {
    setDefinition(normalizeDefinition(rawDef))
  }, [])

  const handleSave = useCallback(() => {
    onSave(definition)
  }, [onSave, definition])

  const isDirtyRef = useRef(isDirty)
  isDirtyRef.current = isDirty

  useEffect(() => {
    const handler = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 's') {
        e.preventDefault()
        if (isDirty && definition.actionPipelineCode) {
          onSave(definition)
        }
      }
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [isDirty, definition, onSave])

  useEffect(() => {
    const handler = (e) => {
      if (isDirtyRef.current) {
        e.preventDefault()
        e.returnValue = ''
      }
    }
    window.addEventListener('beforeunload', handler)
    return () => window.removeEventListener('beforeunload', handler)
  }, [])

  return (
    <div>
      <Prompt
        when={isDirty}
        message={() => {
          if (!isDirtyRef.current) return true
          return 'You have unsaved changes to the definition. Are you sure you want to leave?'
        }}
      />
      <div className={classes.toolbar}>
        <div className={classes.modeToggle}>
          <button
            type='button'
            className={`${classes.modeBtn} ${editorMode === 'visual' ? classes.modeBtnActive : ''}`}
            onClick={() => setEditorMode('visual')}
          >
            Visual
          </button>
          <button
            type='button'
            className={`${classes.modeBtn} ${editorMode === 'json' ? classes.modeBtnActive : ''}`}
            onClick={() => setEditorMode('json')}
          >
            JSON
          </button>
        </div>
        <SydButton
          variant='primary'
          size='sm'
          onClick={handleSave}
          disabled={!isDirty || !definition.actionPipelineCode}
        >
          Save as New Version
        </SydButton>
      </div>

      {editorMode === 'visual' ? (
        <>
          <div className={classes.section}>
            <PipelineConfig
              actionPipelineCode={definition.actionPipelineCode}
              dataPipelineCode={definition.dataPipelineCode}
              onChange={updateField}
            />
          </div>

          <div className={classes.section}>
            <FieldMappingBuilder
              fieldMapping={definition.fieldMapping}
              onChange={(fm) => updateField('fieldMapping', fm)}
            />
          </div>

          <div className={classes.bottomRow}>
            <ArgMappingEditor
              argMapping={definition.argMapping}
              onChange={(am) => updateField('argMapping', am)}
            />
            <InvalidationsEditor
              invalidations={definition.invalidations}
              onChange={(inv) => updateField('invalidations', inv)}
            />
          </div>
        </>
      ) : (
        <RawDefinitionEditor
          definition={definition}
          onChange={handleRawChange}
        />
      )}
    </div>
  )
}

DefinitionTab.propTypes = {
  template: PropTypes.object.isRequired,
  onSave: PropTypes.func.isRequired,
  onDefinitionChange: PropTypes.func,
  initialDefinition: PropTypes.object
}

export default DefinitionTab
